import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../../shared/ui'
import {
  enrollContacts,
  fetchContactIdsForFilters,
  listCustomFieldDefs,
  loadSavedViews,
} from '../../lib/outreach/api'
import {
  EMPTY_VIEW_FILTERS,
  describeFilters,
  isUnfiltered,
  viewToFilters,
} from '../../lib/outreach/savedViews'
import type { ViewFilters } from '../../lib/outreach/savedViews'
import type { CustomFieldDef, EnrollResult, Program } from '../../lib/outreach/types'
import { getStages } from '../../lib/stages'
import { toast } from '../../lib/toast'
import { EnrollFilters, EnrollStyles } from './EnrollFilters'

export type EnrollSource = { kind: 'ids'; ids: string[] } | { kind: 'view' }

export interface EnrollDialogProps {
  programs: Program[]
  defaultProgramId?: string
  source: EnrollSource
  userId: string
  onClose: () => void
  onDone?: (result: EnrollResult) => void
}

/** Matches the default `max` in fetchContactIdsForFilters. */
const MAX_IDS = 5000

const FOCUSABLE = 'select, input:not([disabled]), button:not([disabled]), textarea'

interface Counted {
  key: string
  ids: string[]
  truncated: boolean
}

export function enrollSentences(r: EnrollResult): string {
  const parts = [`Enrolled ${r.enrolled}.`]
  if (r.already_enrolled) {
    parts.push(
      `${r.already_enrolled} ${r.already_enrolled === 1 ? 'was' : 'were'} already in this program.`,
    )
  }
  if (r.no_email) {
    parts.push(`${r.no_email} ${r.no_email === 1 ? 'has' : 'have'} no email address.`)
  }
  if (r.not_found) parts.push(`${r.not_found} not found.`)
  return parts.join(' ')
}

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Something went wrong.'
}

export function EnrollDialog({
  programs,
  defaultProgramId,
  source,
  userId,
  onClose,
  onDone,
}: EnrollDialogProps): JSX.Element {
  const [programId, setProgramId] = useState(
    programs.some((p) => p.id === defaultProgramId)
      ? (defaultProgramId ?? '')
      : (programs[0]?.id ?? ''),
  )
  const [manual, setManual] = useState(false)
  const [viewName, setViewName] = useState('')
  const [custom, setCustom] = useState<ViewFilters>(EMPTY_VIEW_FILTERS)
  const [defs, setDefs] = useState<CustomFieldDef[]>([])
  const [counted, setCounted] = useState<Counted | null>(null)
  const [everyone, setEveryone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<EnrollResult | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const isView = source.kind === 'view'
  const views = useMemo(() => (isView ? loadSavedViews() : []), [isView])
  const stages = useMemo(() => getStages(), [])

  useEffect(() => {
    const root = dialogRef.current
    const first =
      root?.querySelector<HTMLElement>('.modal-body select, .modal-body input') ??
      root?.querySelector<HTMLElement>(FOCUSABLE)
    first?.focus()
  }, [])

  useEffect(() => {
    if (!isView) return
    let live = true
    listCustomFieldDefs('contact')
      .then((d) => live && setDefs(d))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [isView])

  const filters: ViewFilters = useMemo(() => {
    const saved = views.find((v) => v.name === viewName)
    if (saved) return viewToFilters(saved)
    const cf = custom.customField
    return { ...custom, customField: cf && cf.key && cf.value.trim() ? cf : null }
  }, [views, viewName, custom])
  const filterKey = JSON.stringify(filters)
  const chips = describeFilters(filters, defs)
  const unfiltered = isView && isUnfiltered(filters)

  const current = counted && counted.key === filterKey ? counted : null
  const ids = source.kind === 'ids' ? source.ids : (current?.ids ?? [])

  const count = async () => {
    setError('')
    setBusy(true)
    try {
      const r = await fetchContactIdsForFilters(filters, userId, defs)
      setCounted({ key: filterKey, ids: r.ids, truncated: r.truncated })
    } catch (e) {
      setError(message(e))
      toast(message(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    setError('')
    setBusy(true)
    try {
      const r = await enrollContacts(programId, ids, manual)
      setResult(r)
      toast(enrollSentences(r), 'success')
      onDone?.(r)
    } catch (e) {
      setError(message(e))
      toast(message(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab' || !dialogRef.current) return
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const needsEveryone = unfiltered && !everyone
  const canEnroll = Boolean(programId) && ids.length > 0 && !busy && !needsEveryone

  return createPortal(
    <div className="modal-overlay open" onKeyDown={onKeyDown}>
      <EnrollStyles />
      <div
        className="modal modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ed-title"
        ref={dialogRef}
      >
        <div className="modal-header">
          <div className="modal-title" id="ed-title">
            Enroll contacts
          </div>
          <button type="button" className="modal-close" aria-label="Close dialog" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body ed-stack">
          {result ? (
            <p role="status">{enrollSentences(result)}</p>
          ) : programs.length === 0 ? (
            <p className="ed-note">Create a program first, then enroll contacts into it.</p>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label" htmlFor="ed-program">
                  Program
                </label>
                <select
                  id="ed-program"
                  className="form-select"
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                >
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.active ? '' : ' (off)'}
                    </option>
                  ))}
                </select>
              </div>

              {source.kind === 'ids' ? (
                <p>
                  {source.ids.length} selected {source.ids.length === 1 ? 'contact' : 'contacts'}
                </p>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label" htmlFor="ed-view">
                      Contacts
                    </label>
                    <select
                      id="ed-view"
                      className="form-select"
                      value={viewName}
                      onChange={(e) => {
                        setViewName(e.target.value)
                        setEveryone(false)
                      }}
                    >
                      <option value="">Custom filter</option>
                      {views.map((v) => (
                        <option key={v.name} value={v.name}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!viewName && (
                    <EnrollFilters
                      filters={custom}
                      onChange={setCustom}
                      defs={defs}
                      stages={stages}
                    />
                  )}
                  {chips.length > 0 && (
                    <div className="ed-chips" aria-label="Active filters">
                      {chips.map((c, i) => (
                        <span className="ed-chip" key={i}>
                          {c.label}: {c.value}
                        </span>
                      ))}
                    </div>
                  )}
                  {unfiltered && (
                    <div className="ed-stack">
                      <p className="ed-warn">
                        No filter is set. This would enroll every contact that has an email address.
                      </p>
                      <label className="ed-check" htmlFor="ed-everyone">
                        <input
                          id="ed-everyone"
                          type="checkbox"
                          checked={everyone}
                          onChange={(e) => setEveryone(e.target.checked)}
                        />
                        I mean everyone
                      </label>
                    </div>
                  )}
                  <div>
                    <Button variant="secondary" disabled={busy} onClick={() => void count()}>
                      Count matches
                    </Button>
                  </div>
                  {current && (
                    <p role="status">
                      {current.ids.length.toLocaleString()}{' '}
                      {current.ids.length === 1 ? 'contact matches' : 'contacts match'}.
                    </p>
                  )}
                  {current?.truncated && (
                    <p className="ed-warn">
                      More than {MAX_IDS.toLocaleString()} contacts match. Only the first{' '}
                      {MAX_IDS.toLocaleString()} will be enrolled. Narrow the filter to reach the
                      rest.
                    </p>
                  )}
                </>
              )}

              <div className="ed-stack">
                <label className="ed-check" htmlFor="ed-manual">
                  <input
                    id="ed-manual"
                    type="checkbox"
                    checked={manual}
                    onChange={(e) => setManual(e.target.checked)}
                  />
                  Manual: ignore the daily cap for these contacts
                </label>
                <div className="ed-note">
                  They are sent in the next run regardless of the cap. Suppressions, send days and
                  the bounce stop still apply.
                </div>
              </div>
              {error && (
                <div className="ed-error" role="alert">
                  {error}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          {result || programs.length === 0 ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!canEnroll} onClick={() => void confirm()}>
                {busy
                  ? 'Working...'
                  : `Enroll ${ids.length.toLocaleString()} ${ids.length === 1 ? 'contact' : 'contacts'}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
